import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useState, useEffect } from 'react';

/**
 * Custom hook that combines useQuery with local state management.
 * It handles the common pattern of fetching data with useQuery and setting it to local state.
 */
export function useQueryWithState<TData, TError = Error>(
  queryKey: any[],
  queryFn: () => Promise<TData>,
  options: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    /** Antal omförsök vid fel. false = inga, number = max antal, eller (failureCount, error) => boolean */
    retry?: boolean | number | ((failureCount: number, error: TError) => boolean);
  } = {}
): [TData | undefined, boolean, TError | null, React.Dispatch<React.SetStateAction<TData | undefined>>, UseQueryResult<TData, TError>] {
  // Set default options
  const defaultOptions = {
    staleTime: 0,
    refetchOnMount: true,
    ...options
  };

  // Execute the query
  const query = useQuery<TData, TError>({
    queryKey,
    queryFn,
    ...defaultOptions
  });

  // Maintain local state that matches the query data
  const [data, setData] = useState<TData | undefined>(undefined);

  // Update local state when query data changes
  useEffect(() => {
    if (query.data !== undefined) {
      setData(query.data);
    }
  }, [query.data]);

  // Return the data, loading state, error, setter function, and the query result
  return [data, query.isFetching, query.error || null, setData, query];
} 